import * as React from "react"
import * as Sentry from "@sentry/react"
import { MessageSquareText } from "lucide-react"

import { NavMainItem, NavMainV2 } from "@/components/nav-main"
import { AvatarUser } from "@/components/avatar-user"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar"
import { UserInfoResponse, UserInfoResponseRole } from "@/services/generatedAPIs.schemas"
import { useTranslation } from "react-i18next"
import { isEN } from "@/utils/i18n"

// 设计图侧边栏 logo：橙色品牌图标 + 黑色字样（1:1 取自 Figma）
// 中文「易學平台」，英文沿用既有品牌字样「EASE」（统一换成橙色图标+黑字以适配浅色侧边栏）
import Logo from '@/assets/images/logo.svg'
import LogoEn from '@/assets/images/logo_en.svg'
// 非学生角色还原 github/dev 侧边栏用的中文 logo（深绿底白字版）
import LogoZh from '@/assets/images/logo_zh.svg'
// UGC + 香港教育大學 logo（Figma 源中 UGC 半边为位图，整体导出为 4x PNG 以避免缩放糊化）
import UgcEduhkLogo from '@/assets/practice/ugc-eduhk-logo.png'

type AppSidebarProps = {
  user: UserInfoResponse,
  panelItem: NavMainItem
}

export function AppSidebar({ user, panelItem, ...props }: React.ComponentProps<typeof Sidebar> & AppSidebarProps) {
  const { t, i18n } = useTranslation();

  // 打开既有的 Sentry 意见反馈表单（仅生产环境初始化；开发环境无操作）
  const openFeedback = async () => {
    try {
      const feedback = Sentry.getFeedback?.();
      if (!feedback) return;
      const form = await feedback.createForm();
      form.appendToDom();
      form.open();
    } catch (e) {
      console.warn("Feedback widget unavailable:", e);
    }
  };

  // 非学生角色（教师等）：还原 github/dev 侧边栏（深绿底 bg-primary + 内容区圆角/边框 +
  // logo_zh/en + 白色头像文字 + 无 Feedback footer）。dev-cpp 已把深绿/圆角/边框从
  // ui/sidebar primitive 删除，这里用 className 重新加回，不动 primitive。
  const isStudent = user.role === UserInfoResponseRole.STUDENT;
  if (!isStudent) {
    return (
      <Sidebar collapsible="icon" {...props} className={["bg-primary", props.className].filter(Boolean).join(" ")}>
        <SidebarHeader className="mt-10">
          <img
            src={isEN(i18n) ? LogoEn : LogoZh}
            className="absolute scale-x-100 -top-4"
          />
          <AvatarUser user={user as any} legacy />
        </SidebarHeader>
        <SidebarContent className="mt-4 bg-background rounded-t-4xl border-r border-primary">
          <NavMainV2 items={panelItem} legacy />
        </SidebarContent>
        <SidebarRail />
      </Sidebar>
    )
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      {/* mt-10 是 1920 设计稿留给 logo+avatar 的顶部余量,矮视口(<1920)收到 mt-4 释放空间防止滚动条 */}
      <SidebarHeader className="mt-4 min-[1920px]:mt-10">
        {/* Figma 1920 设计稿 logo 179×54 (中文 SVG 178×54 / 英文 SVG 187×54, viewBox h=54),
            ≥1920 用 h-[54px] 精确还原 Figma; 笔电中档 xl 给 h-11 (44px) 过渡,
            默认小屏维持 h-9 (36px) 紧凑.
            水平: ≥1920 维持 Figma 原貌 —— logo 左边对齐菜单图标左边 (而非 sidebar 居中),
              图标左 = SidebarGroup-px + Card-pl×CardWidth = 35px+18%×(sidebar-70)=calc(22.4px+18%)。
            <1920 小屏(平板/笔电): 改为侧边栏内水平居中 (left-1/2 + -translate-x-1/2),
              因窄侧边栏下菜单缩进比例变小, 沿用左对齐会让 logo 明显偏左失衡 (用户要求只在小屏居中)。
            注: min-[1920px] 任意断点在 Tailwind v4 cascade 上排在 xl 之前,
            必须用 ! 才能盖过 xl:h-11 等 (同文件 Feedback 区 max-w-[328px]! 是同一原因);
            ≥1920 用 translate-x-0! 抵消小屏的居中位移, 还原 Figma 左对齐. */}
        <img
            src={isEN(i18n) ? LogoEn : Logo}
            alt="易學平台"
            className="absolute top-2 left-1/2 -translate-x-1/2 h-9 w-auto xl:h-11 min-[1920px]:left-[calc(22.4px+18%)]! min-[1920px]:translate-x-0! min-[1920px]:h-[54px]!"
        />
        {/* <NavUser user={props.user as any} /> */}
        <AvatarUser user={user as any} />
      </SidebarHeader>
      <SidebarContent className="mt-4">
        <NavMainV2 items={panelItem} />
      </SidebarContent>
      {/* Figma 1920px 设计稿：Feedback 距底 34px；按钮宽与选中菜单项一致。
          SidebarFooter 基类含 p-2，会把内子元素 % 宽度的分母从 sidebar(415) 缩成 399，
          导致 w-[84%] 实际只有 335 而不是设计稿的 348。这里 px-0 抹掉左右 padding，
          让按钮百分比和 NavMainV2 group 的 px-[%] 同分母（=sidebar 自身宽）。
          矮视口(<1920) 把 gap / pb-[34px] 收紧到 gap-2 / pb-3, 释放 ~40px 防滚动条;
          ≥1920 维持 Figma 原貌：UGC logo 与 Feedback 按钮间距 28px(gap-7), 距底 34px.
          1024(lg)档按设计稿单独放宽: 距底 pb-5(20px) + 按钮圆角 15px(xl/2xl 仍 pb-3/12). */}
      <SidebarFooter className="items-center gap-2 px-0 pb-3 lg:pb-5 xl:pb-3 group-data-[collapsible=icon]:hidden min-[1920px]:gap-7 min-[1920px]:pb-[34px]">
        {/* Figma 449:1477(Page2/學習概覽 sidebar 内 UGC+教大 组):
              - 组宽 328 / 高 33 / UGC 204 + 18 gap + 教大 106 (18px gap 已烤入 ugc-eduhk-logo.png, aspect 9.939 = 328/33)
              - sidebar 415 内 left=44 / right=43 视觉居中 (SidebarFooter items-center 已对齐)
              ≥1920 把宽放到 328px (max-w-[328px]!) 才能还原 h=33; 之前 max-w-[260px] 锁出 w=260/h=26 偏小. */}
        <img
          src={UgcEduhkLogo}
          alt="University Grants Committee / The Education University of Hong Kong"
          className="w-[82%] max-w-[260px] min-[1920px]:max-w-[328px]!"
        />
        {/* Feedback 宽恒为 88%(=1920 两侧各 25px 间距等比缩, 各档 6% 不变);
            高度四档与「选中态菜单项」(nav-main.tsx)完全同源 —— 菜单项是 SidebarMenuButton,
            其默认尺寸 h-8(32px) 在 <lg 生效;本按钮是普通 button 没这个默认,故显式补 h-8,
            再叠 xl:h-[62px](62) / ≥1920:h-[83px](83) 与菜单项像素一致; lg(1024)按设计稿单独 h-[42px](42),
            不再随菜单项 48; 圆角/字号/图标逐档同比缩小, lg 字号沿用 base text-sm(14px), ≥1920 用 ! 锁回 Figma 原值(h83/r25/text-xl/icon24)不变 */}
        <button
          type="button"
          onClick={openFeedback}
          className="flex h-8 w-[88%] items-center justify-center gap-2 rounded-[14px] text-sm font-semibold shadow-sm transition hover:brightness-95 lg:h-[42px] lg:rounded-[15px] xl:h-[62px] xl:rounded-[18px] xl:text-base 2xl:rounded-[20px] min-[1920px]:h-[83px]! min-[1920px]:w-[88%]! min-[1920px]:rounded-[25px]! min-[1920px]:text-xl!"
          style={{
            backgroundColor: 'var(--practice-feedback-bg)',
            color: 'var(--practice-feedback-fg)',
          }}
        >
          <MessageSquareText size={18} className="2xl:size-5 min-[1920px]:size-6!" />
          {t('common.feedback')}
        </button>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
